import { WritableSignal } from '@angular/core';
import { RdxDialogSelfRef } from '@radix-ng/primitives/dialog';
import { ButtonVariant } from '../../button/models/button.model';
import { BaseDialogParams } from '../../../models/dialog.model';
import { Subject } from 'rxjs';

type ComponentType<T> = new (...args: any[]) => T;

export enum DialogActionTypeEnum {
  PRIMARY = 'primary',
  DISMISS = 'dismiss',
}

export type DialogActionType = `${DialogActionTypeEnum}`;

// Base interface with common properties
interface DialogActionInputBase {
  /**
   * Type of the action.
   */
  type: DialogActionType;
  /**
   * Callback to be executed when the action is clicked.
   * @param ref - The dialog reference.
   * @param extraData - Extra data to be passed to the callback. this is defined in the
   * component rendered in the dialog.
   */
  callback?: (ref: RdxDialogSelfRef<void>, extraData?: any) => void;
  /**
   * Initial state of the action. it only work if the withActionBindings flag is true.
   */
  disabled?: boolean;
  /** Variant of the action. */
  variant?: ButtonVariant;
}

// Union type ensuring either 'label' or '$label' is required
export type DialogActionInput =
  | (DialogActionInputBase & {
    /**
     * Label of the action.
     */
    label: string;
    /**
     * Signal to update the label of the action.
     */
    $label?: WritableSignal<string>;
  })
  | (DialogActionInputBase & {
    /**
     * Label of the action.
     */
    label?: string;
    /**
     * Signal to update the label of the action.
     */
    $label: WritableSignal<string>;
  });

export interface DialogAction extends Omit<DialogActionInput, 'disabled'> {
  variant: ButtonVariant;
  /**
   * Signal to disable the action.
   * It is used internally by the dialog service and exposed to the rendered component.
  */
  $disabled: WritableSignal<boolean> | undefined;
  /**
   * Trigger the action.
   * It is used internally by the dialog service and exposed to the rendered component.
   */
  trigger: () => void | undefined;
}

export type DialogDataChange<T> = (data: T) => void;
export type DialogEventEmitted = { ref: RdxDialogSelfRef<void> } & ({
  type: 'click';
  action: DialogAction;
} | {
  type: 'close';
} | {
  type: 'data';
  data: any;
});

export type DialogEvent = Subject<DialogEventEmitted>;

export interface DialogParams extends BaseDialogParams {
  component: ComponentType<any>;
  componentInputs?: Record<string, any>;
  actions?: DialogActionInput[];
  /**
   * If true, the dialog will have a border on the header.
   *
   * @default false
   */
  withHeaderBorder?: boolean;
  /**
   * If true, the rendered component will receive the actions as inputs.
   * This is useful for components that need to access the actions, such as a form.
   * Note: If true the component must define an input named 'actions', which is of type DialogAction[].
   *
   * @default false
   */
  withActionBindings?: boolean;
  /**
   * if true, the rendered component will be able to control the actions of the dialog.
   * This is useful for components that need to control the actions of the dialog, such as a form.
   * Note: If true the component must define an input named 'actions', which is of type DialogAction[].
   *
   * @default false
   */
  withControlledActions?: boolean;
  /**
   * If true, the rendered component will be able to change the compact view of the dialog.
   * This is useful for components that need to change the compact view of the dialog, such as a tree view.
   * Note: If true the component must define an input named 'compactViewChange$'.
   *
   * @default false
   */
  withCompactViewChange?: boolean;
  /**
   * If true, the rendered component will be able to listen to the events of the dialog.
   * This is useful for components that need to listen to the events of the dialog, such as a form.
   * Note: If true the component must define an input named 'events$'.
   *
   * @default false
   */
  withEventsBindings?: boolean;
  /**
   * If true, the rendered component will receive a callback to communicate data back to the dialog.
   * Note: If true the component must define an input named 'dataChange', which is of type DialogDataChange<T>.
   *
   * @default false
   */
  withDataBindings?: boolean;

  /**
   * If true, the rendered component will receive the dialog reference.
   * This is useful for components that need to communicate with the dialog, such as a form.
   * Note: If true the component must define an input named 'dialogRef', which is of type RdxDialogSelfRef<void>.
   *
   * @default false
   */
  withDialogRef?: boolean;

  /**
   * Minimum width of the dialog.
   *
   * @default 400px
   */
  minWidth?: string | number;
  /**
   * Width of the dialog.
   *
   * @default undefined
   */
  width?: string | number;
  /**
   * Maximum width of the dialog.
   *
   * @default undefined
   */
  maxWidth?: string | number;
  /**
   * Minimum height of the dialog.
   *
   * @default undefined
   */
  minHeight?: string | number;
  /**
   * Maximum height of the dialog.
   *
   * @default undefined
   */
  maxHeight?: string | number;
  /**
   * Height of the dialog.
   *
   * @default undefined
   */
  height?: string | number;

  margin?: string | number;

  headerStyles?: Record<string, string>;
  footerStyles?: Record<string, string>;
  bodyStyles?: Record<string, string>;
}
